import { Component, DestroyRef, inject } from '@angular/core';
import { TsButtonModule, TsPageHeaderModule, TsPaginatorChangeEvent, TsPaginatorModule } from 'tsui';
import { BehaviorSubject, Observable, debounceTime, shareReplay, switchMap, tap, map } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { AuditListComponent } from './list/list.component';
import { AccountService } from '@core/services';
import { AccountState } from '@state/account/state';
import { Store } from '@ngxs/store';
import { PaginatedResource } from '@shared/models/paginated-resource';
import { Activity } from '@core/models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'audit',
  standalone: true,
  imports: [TsPageHeaderModule, TsButtonModule, TsPaginatorModule, AsyncPipe, AuditListComponent],
  templateUrl: './audit.component.html',
})
export class AuditSettingsComponent {
  private readonly store = inject(Store);
  private readonly accountService = inject(AccountService);
  private readonly destroyRef = inject(DestroyRef);
  private searchTrigger = new BehaviorSubject<{ page: number; per_page: number }>({
    page: 1,
    per_page: 20,
  });

  readonly loading = new BehaviorSubject(true);

  private get accountId(): string {
    return this.store.selectSnapshot(AccountState.getAccountId);
  }

  activitiesPage$: Observable<PaginatedResource<Activity>> = this.searchTrigger.pipe(
    debounceTime(300),
    tap(() => this.loading.next(true)),
    switchMap(({ page, per_page }) =>
      this.accountService.getActivities({ account_id: this.accountId, page, per_page }),
    ),
    map((response) => {
      if (Array.isArray(response)) {
        return {
          results: response,
          count: response.length,
          page: 1,
          per_page: response.length,
          last_page: 1,
        } as PaginatedResource<Activity>;
      }
      return response;
    }),
    tap(() => this.loading.next(false)),
    takeUntilDestroyed(this.destroyRef),
  );

  pageChanged({ activePage, pageSize }: TsPaginatorChangeEvent) {
    this.searchTrigger.next({ page: activePage, per_page: pageSize });
  }
}
